import { useState, useEffect } from 'react';
import {
  HiOutlineClipboardList,
  HiOutlineTruck,
  HiOutlineCash,
  HiOutlineClock,
} from 'react-icons/hi';
import { getUser, getDashboardStats, getDashboardActivity, getInventory } from '../utils/api';
import { formatModelCode } from '../utils/modelCategoryRanges';

const ACTIVITY_TYPE_LABELS = {
  order: 'Order',
  payment: 'Payment',
  inventory: 'Inventory',
  inquiry: 'Inquiry',
};

function formatRelativeTime(timestamp) {
  const then = new Date(timestamp).getTime();
  const now = Date.now();
  const diffSec = Math.floor((now - then) / 1000);

  if (diffSec < 60) return 'Just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 172800) return 'Yesterday';

  return new Date(timestamp).toLocaleDateString('en-IN', {
    month: 'short',
    day: 'numeric',
  });
}

function StockHealthDonut({ healthy, low, outOfStock, total, available, avgHealth }) {
  const safeTotal = total || 1;
  const healthyPct = (healthy / safeTotal) * 100;
  const lowPct = (low / safeTotal) * 100;
  const outPct = (outOfStock / safeTotal) * 100;
  const gradient = total === 0
    ? 'conic-gradient(#e5e7eb 0% 100%)'
    : `conic-gradient(
        #d1d5db 0% ${healthyPct}%,
        #fbbf24 ${healthyPct}% ${healthyPct + lowPct}%,
        #f87171 ${healthyPct + lowPct}% 100%
      )`;

  return (
    <div className="flex flex-col items-center">
      <div
        className="relative h-36 w-36 rounded-full"
        style={{ background: gradient }}
        role="img"
        aria-label={`Stock health: ${healthy} healthy, ${low} low, ${outOfStock} out of stock`}
      >
        <div className="absolute inset-4 flex flex-col items-center justify-center rounded-full bg-white text-center">
          <span className="text-2xl font-bold text-gray-900">{available}</span>
          <span className="text-xs text-gray-500">Available</span>
          {avgHealth > 0 && (
            <span className="mt-0.5 text-[10px] text-gray-400">{avgHealth}% avg stock</span>
          )}
        </div>
      </div>
      <ul className="mt-4 w-full space-y-1.5 text-xs text-gray-600">
        <li className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-gray-300" aria-hidden="true" />
            In stock
          </span>
          <span className="font-medium text-gray-900">{healthy}</span>
        </li>
        <li className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400" aria-hidden="true" />
            Low stock
          </span>
          <span className="font-medium text-gray-900">{low}</span>
        </li>
        <li className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-red-400" aria-hidden="true" />
            Out of stock
          </span>
          <span className="font-medium text-gray-900">{outOfStock}</span>
        </li>
      </ul>
      {outPct > 0 && (
        <p className="mt-2 text-[10px] text-gray-400">{Math.round(outPct)}% of catalog unavailable</p>
      )}
    </div>
  );
}

function CategoryBarChart({ data }) {
  const maxCount = Math.max(...data.map((d) => d.count), 1);

  if (data.length === 0) {
    return <p className="text-sm text-gray-500">No category data</p>;
  }

  return (
    <ul className="space-y-3">
      {data.map(({ size, count }) => (
        <li key={size}>
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="capitalize text-gray-600">{size}</span>
            <span className="font-medium text-gray-900">{count}</span>
          </div>
          <div className="h-2 w-full rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-slate-600 transition-all duration-300"
              style={{ width: `${(count / maxCount) * 100}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

const OverviewPage = () => {
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalDispatched: 0,
    totalAdvanceReceived: 0,
    totalBalancePending: 0,
    lowStockCount: 0,
  });
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [statsData, activityData, inventoryData] = await Promise.all([
          getDashboardStats(),
          getDashboardActivity(),
          getInventory(),
        ]);
        setStats(statsData);
        setActivities(activityData.activities || []);
        setInventory(inventoryData);
        await getUser();
      } catch (error) {
        console.error('Error fetching overview data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (amount) => `₹${amount.toLocaleString('en-IN')}`;

  const totalItems = inventory.length;
  const lowStockItems = inventory.filter((item) => item.currentStock <= 5 && item.currentStock > 0).length;
  const outOfStockItems = inventory.filter((item) => item.currentStock === 0).length;
  const availableItems = totalItems - outOfStockItems;
  const healthyInStock = inventory.filter((item) => item.currentStock > 5).length;

  const totalRetailValue = inventory.reduce((sum, item) => sum + item.retailPrice * item.currentStock, 0);
  const totalWholesaleValue = inventory.reduce((sum, item) => sum + item.wholesalePrice * item.currentStock, 0);
  const averageRetailValue = totalItems > 0 ? Math.round(totalRetailValue / totalItems) : 0;

  const totalStockPercentage = inventory.reduce((sum, item) => {
    const percentage = item.initialStock > 0 ? (item.currentStock / item.initialStock) * 100 : 0;
    return sum + percentage;
  }, 0);
  const averageStockHealth = totalItems > 0 ? Math.round(totalStockPercentage / totalItems) : 0;

  const categories = [...new Set(inventory.map((item) => item.size))].filter(Boolean).sort();

  const selectedCategoryItems = selectedCategory === 'all'
    ? inventory
    : inventory.filter((item) => item.size === selectedCategory);

  const bestPerformingModel = selectedCategoryItems.length > 0
    ? selectedCategoryItems.reduce((best, current) => (current.sold > best.sold ? current : best))
    : null;

  const stockByCategory = categories.map((size) => ({
    size,
    count: inventory.filter((i) => i.size === size).length,
  }));

  const criticalAlerts = [];
  if (outOfStockItems > 0) criticalAlerts.push(`${outOfStockItems} out of stock`);
  if (lowStockItems > 0) criticalAlerts.push(`${lowStockItems} low stock`);
  const overstockedItems = inventory.filter((item) => item.currentStock > item.initialStock * 1.5).length;
  if (overstockedItems > 0) criticalAlerts.push(`${overstockedItems} overstocked`);

  const dispatchPct = stats.totalOrders
    ? Math.min(100, (stats.totalDispatched / stats.totalOrders) * 100)
    : 0;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-slate-700" />
          <span className="font-medium text-gray-600">Loading overview...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Welcome */}
      <div className="mb-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="mb-1 text-2xl font-bold text-gray-900 sm:text-3xl">Welcome back</h1>
            <p className="text-gray-600">Here&apos;s your business overview for today</p>
          </div>
          <div className="hidden shrink-0 text-right sm:block">
            <img src="/logo.png" alt="Ekdant Arts" className="mx-auto mb-1 h-12 w-12" />
            <p className="text-xs text-gray-500">Ganesh Idol Booking System</p>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <p className="mb-1 text-sm font-medium text-gray-500">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
            </div>
            <HiOutlineClipboardList className="h-6 w-6 shrink-0 text-gray-400" aria-hidden="true" />
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <p className="mb-1 text-sm font-medium text-gray-500">Orders Dispatched</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalDispatched} / {stats.totalOrders}
              </p>
              <p className="mt-1 text-xs text-gray-500">Dispatched</p>
              <div className="mt-2 h-1.5 w-full rounded-full bg-gray-100">
                <div
                  className="h-full rounded-full bg-slate-700 transition-all duration-300"
                  style={{ width: `${dispatchPct}%` }}
                />
              </div>
            </div>
            <HiOutlineTruck className="h-6 w-6 shrink-0 text-gray-400" aria-hidden="true" />
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <p className="mb-1 text-sm font-medium text-gray-500">Advance Received</p>
              <p className="text-2xl font-bold text-emerald-800">{formatCurrency(stats.totalAdvanceReceived)}</p>
            </div>
            <HiOutlineCash className="h-6 w-6 shrink-0 text-gray-400" aria-hidden="true" />
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <p className="mb-1 text-sm font-medium text-gray-500">Total Outstanding (All Orders)</p>
              <p className="text-2xl font-bold text-amber-700">{formatCurrency(stats.totalBalancePending)}</p>
            </div>
            <HiOutlineClock className="h-6 w-6 shrink-0 text-gray-400" aria-hidden="true" />
          </div>
        </div>
      </div>

      {criticalAlerts.length > 0 && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {criticalAlerts.join(' · ')}
        </div>
      )}

      <div className="lg:grid lg:grid-cols-3 lg:gap-6">
        {/* Main column */}
        <div className="space-y-6 lg:col-span-2">
          {/* Inventory charts */}
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-6 text-lg font-semibold text-gray-900">Inventory</h2>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              <div>
                <h3 className="mb-4 text-sm font-medium text-gray-500">Stock health</h3>
                <StockHealthDonut
                  healthy={healthyInStock}
                  low={lowStockItems}
                  outOfStock={outOfStockItems}
                  total={totalItems}
                  available={availableItems}
                  avgHealth={averageStockHealth}
                />
              </div>
              <div>
                <h3 className="mb-4 text-sm font-medium text-gray-500">Models by category</h3>
                <CategoryBarChart data={stockByCategory} />
                <p className="mt-4 text-xs text-gray-400">{totalItems} total models in catalog</p>
              </div>
            </div>
          </div>

          {/* Stock value + best model */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h4 className="mb-3 text-sm font-semibold text-gray-900">Stock Value</h4>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-600">Retail</dt>
                  <dd className="font-semibold text-gray-900">{formatCurrency(totalRetailValue)}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-gray-600">Wholesale</dt>
                  <dd className="font-semibold text-gray-900">{formatCurrency(totalWholesaleValue)}</dd>
                </div>
                <div className="flex justify-between gap-3 border-t border-gray-200 pt-2">
                  <dt className="text-gray-600">Avg / item</dt>
                  <dd className="font-semibold text-gray-900">{formatCurrency(averageRetailValue)}</dd>
                </div>
              </dl>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <h4 className="mb-3 text-sm font-semibold text-gray-900">Best Performing Model</h4>
              <div className="mb-3">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-slate-500"
                >
                  <option value="all">All Categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                <p className="mt-1.5 text-xs text-gray-500">
                  {selectedCategoryItems.length} items in category
                </p>
              </div>

              {bestPerformingModel ? (
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <dl className="grid grid-cols-1 gap-2.5 text-sm">
                    <div className="flex justify-between gap-3 border-b border-gray-100 pb-2">
                      <dt className="text-gray-600">Model</dt>
                      <dd className="text-right font-semibold text-gray-900">
                        {formatModelCode(bestPerformingModel.size, bestPerformingModel.modelNumber)}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-600">Sold</dt>
                      <dd className="font-semibold text-gray-900">{bestPerformingModel.sold}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt className="text-gray-600">Stock remaining</dt>
                      <dd className={`font-semibold ${bestPerformingModel.currentStock === 0 ? 'text-red-600' : bestPerformingModel.currentStock <= 5 ? 'text-amber-600' : 'text-gray-900'}`}>
                        {bestPerformingModel.currentStock}
                      </dd>
                    </div>
                    <div className="flex justify-between gap-3 border-t border-gray-100 pt-2">
                      <dt className="text-gray-600">Value</dt>
                      <dd className="font-semibold text-gray-900">
                        {formatCurrency(bestPerformingModel.sold * bestPerformingModel.retailPrice)}
                      </dd>
                    </div>
                  </dl>
                  <p className="mt-2 truncate text-xs text-gray-500" title={bestPerformingModel.modelName}>
                    {bestPerformingModel.modelName}
                  </p>
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 bg-white p-3 text-center text-xs text-gray-500">
                  No sales data available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Activity sidebar */}
        <div className="mt-6 lg:col-span-1 lg:mt-0">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm lg:sticky lg:top-6">
            <h2 className="mb-6 text-lg font-semibold text-gray-900">Recent Activity</h2>
            {activities.length === 0 ? (
              <p className="text-sm text-gray-500">No recent activity</p>
            ) : (
              <div className="relative ml-3 space-y-6 border-l-2 border-gray-200">
                {activities.map((item, index) => (
                  <div key={item.id} className="relative pl-6">
                    <span
                      className={`absolute -left-[9px] top-1 h-3.5 w-3.5 rounded-full border-2 border-white ${index === 0 ? 'bg-slate-700' : 'bg-gray-300'}`}
                      aria-hidden="true"
                    />
                    <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
                      {ACTIVITY_TYPE_LABELS[item.type] || item.type}
                    </p>
                    <p className="mt-0.5 text-sm leading-snug text-gray-800">{item.message}</p>
                    <p className="mt-1 text-xs text-gray-500">{formatRelativeTime(item.timestamp)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewPage;
